import React, { FC } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import {
  AssistMigrationDistributionPreview,
  AssistMigrationPreview,
} from 'types/assist/assistMigration';
import { DistributionEditor } from './DistributionEditor';

interface MigrationPreviewProps {
  preview: AssistMigrationPreview;
  distribution: AssistMigrationDistributionPreview | null;
  validationErrors: string[];
  onUpdateBusinessSettings: (
    patch: Partial<AssistMigrationDistributionPreview['businessSettings']>,
  ) => void;
  onUpdateGlobal: (patch: Partial<AssistMigrationDistributionPreview['global']>) => void;
  onUpdateStore: (
    storeId: number,
    patch: Partial<AssistMigrationDistributionPreview['stores'][number]>,
  ) => void;
}

export const MigrationPreview: FC<MigrationPreviewProps> = ({
  preview,
  distribution,
  validationErrors,
  onUpdateBusinessSettings,
  onUpdateGlobal,
  onUpdateStore,
}) => (
  <div>
    {preview.warnings.map((warning) => (
      <Alert key={warning} severity="warning" style={{ marginBottom: 8 }}>
        {warning}
      </Alert>
    ))}

    <Accordion
      disableGutters
      sx={{ boxShadow: 'none', '&:before': { display: 'none' }, background: 'transparent' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 36 }}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Steps ({preview.profile.storeCount} store(s))
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        {preview.actions.map((action, index) => (
          <div
            key={`${action.type}-${index}`}
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}
          >
            <Chip
              size="small"
              label={action.alreadySatisfied ? 'already done' : 'will run'}
              color={action.alreadySatisfied ? 'default' : 'primary'}
              style={{ flexShrink: 0 }}
            />
            <Typography variant="body2" style={{ fontWeight: 600, flexShrink: 0 }}>
              {action.title}
            </Typography>
            {action.detail ? (
              <Typography variant="caption" color="textSecondary" noWrap style={{ minWidth: 0 }}>
                {action.detail}
              </Typography>
            ) : null}
          </div>
        ))}
      </AccordionDetails>
    </Accordion>

    {validationErrors.length > 0 && (
      <Alert severity="error" style={{ margin: '12px 0' }}>
        <AlertTitle>Resolve before running the migration</AlertTitle>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {validationErrors.map((error) => (
            <li key={error}>
              <Typography variant="caption">{error}</Typography>
            </li>
          ))}
        </ul>
      </Alert>
    )}

    <Divider style={{ margin: '16px 0' }} />

    {distribution ? (
      <DistributionEditor
        source={preview.source}
        distribution={distribution}
        defaultFaqQuestions={preview.defaultFaqQuestions}
        onUpdateBusinessSettings={onUpdateBusinessSettings}
        onUpdateGlobal={onUpdateGlobal}
        onUpdateStore={onUpdateStore}
      />
    ) : (
      <Typography variant="caption" color="textSecondary">
        New per-location settings are generated during the migration.
      </Typography>
    )}
  </div>
);
